import styled from "styled-components";

/**
 * Segmented icon toggle — a single grouped control that swaps between a small
 * set of views (e.g. board / list). Replaces the older pair of bordered
 * buttons: a light track holds icon-only segments, the active one lifting to a
 * white pill with a soft shadow and brand-coloured icon. Slim line icons only.
 */
export interface ViewToggleOption {
  value: string;
  icon: React.ReactNode;
  /** Accessible label / tooltip for the segment. */
  label: string;
}

interface ViewToggleProps {
  value: string;
  onChange: (value: string) => void;
  options: ViewToggleOption[];
}

const Track = styled.div`
  ${({ theme: { colors } }) => `
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background: ${colors.gray[10]};
    border: 1px solid ${colors.gray[100]};
    border-radius: 10px;
  `}
`;

const Segment = styled.button<{ isActive: boolean }>`
  ${({ theme: { colors, shadows }, isActive }) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 7px;
    cursor: pointer;
    font-size: 18px;
    line-height: 0;
    background: ${isActive ? colors.white : "transparent"};
    color: ${isActive ? colors.blue.primary : colors.gray[50]};
    box-shadow: ${isActive ? shadows.sm : "none"};
    transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      color: ${isActive ? colors.blue.primary : colors.gray[60]};
    }

    &:focus,
    &:focus-visible {
      outline: none;
    }
  `}
`;

export const ViewToggle = ({ value, onChange, options }: ViewToggleProps) => (
  <Track role="tablist">
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <Segment
          key={option.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-label={option.label}
          title={option.label}
          isActive={isActive}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
        </Segment>
      );
    })}
  </Track>
);
